// src/modules/quotes/CreateQuoteForm.jsx
import { useState, useEffect, useRef } from "react";
import { C } from "../../constants";
import { Ic } from "../../components/ui/Icons";
import { apiCall } from "../../api";
import FileUpload from "../../components/ui/FileUpload";
import { inputStyle, labelStyle, inputFocus, inputBlur } from "../../components/ui/Components";

export default function CreateQuoteForm({ user, usedRequestIds, onClose, onCreate }) {
  const [form, setForm] = useState({
    quoteNumber: "", requestId: "", expiryDate: "",
    solutionType: "", projectName: "",
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showAbandon, setShowAbandon] = useState(false);
  const [showNoEmailWarning, setShowNoEmailWarning] = useState(false);

  // ── Quote Number validation state ──
  const [quoteInput, setQuoteInput] = useState("");
  const [quoteValidating, setQuoteValidating] = useState(false);
  const [quoteValidated, setQuoteValidated] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [pipelineData, setPipelineData] = useState(null);

  // Request ID searchable dropdown
  const [requests, setRequests] = useState([]);
  const [reqSearch, setReqSearch] = useState("");
  const [reqDropdown, setReqDropdown] = useState(false);
  const reqRef = useRef(null);

  const isDirty = Object.values(form).some(v => v) || files.length > 0 || quoteInput;

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const r = await apiCall("getRequests", { email: user.email, company: user.company, role: user.role || "ADMIN" });
        if (r.success) setRequests(r.requests || []);
      } catch (err) { console.error("Failed to load requests:", err); }
    };
    fetchRequests();
    const handleClick = (e) => { if (reqRef.current && !reqRef.current.contains(e.target)) setReqDropdown(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRequests = requests.filter(r =>
    !(usedRequestIds && usedRequestIds.has(r.id)) &&
    (!reqSearch || r.id.toLowerCase().includes(reqSearch.toLowerCase()) ||
    (r.type || "").toLowerCase().includes(reqSearch.toLowerCase()) ||
    (r.contactName || "").toLowerCase().includes(reqSearch.toLowerCase()) ||
    (r.company || "").toLowerCase().includes(reqSearch.toLowerCase()))
  );

  const selectRequest = (req) => {
    setForm(f => ({
      ...f,
      requestId: req.id,
      solutionType: req.type || f.solutionType,
    }));
    setReqSearch(req.id + (req.type ? ` — ${req.type}` : ""));
    setReqDropdown(false);
  };

  const handleClose = () => {
    if (isDirty) { setShowAbandon(true); } else { onClose(); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Quote Number: validate against pipeline ──
  const handleQuoteValidate = async () => {
    const qn = quoteInput.trim();
    if (!qn) { setQuoteError("Please enter a quote number."); return; }
    setQuoteValidating(true);
    setQuoteError("");
    try {
      const r = await apiCall("lookupQuote", { quoteNumber: qn });
      if (r.success && r.found) {
        setQuoteValidated(true);
        setPipelineData(r.data);
        setForm(f => ({ ...f, quoteNumber: qn }));
        setQuoteError("");
      } else {
        setQuoteValidated(false);
        setPipelineData(null);
        setForm(f => ({ ...f, quoteNumber: "" }));
        setQuoteError(r.error || "Quote number not found in the Boal Sales Pipeline. Please verify and try again.");
      }
    } catch (err) {
      setQuoteError("Network error. Please try again.");
    }
    setQuoteValidating(false);
  };

  const handleQuoteClear = () => {
    setQuoteInput("");
    setQuoteValidated(false);
    setPipelineData(null);
    setQuoteError("");
    setForm(f => ({ ...f, quoteNumber: "" }));
  };

  const handleCreate = async () => {
    if (!form.quoteNumber || !quoteValidated) { setError("Please validate the Quote Number first."); return; }
    if (files.length === 0) { setError("Please attach the quote PDF file before creating."); return; }
    setSubmitting(true);
    setError("");
    try {
      const linkedReq = requests.find(r => r.id === form.requestId);
      const r = await apiCall("createQuote", {
        ...form,
        createdBy: user.name,
        company: linkedReq?.company || "",
        requestedBy: linkedReq?.contactName || "",
        files: files.map(f => ({ name: f.name, mimeType: f.mimeType, base64: f.base64 })),
      });
      if (r.success) {
        if (r.submitterEmailFound === false) {
          setShowNoEmailWarning(true);
        }
        onCreate(r.quote);
      } else {
        setError(r.error || "Failed to create quote.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
    setSubmitting(false);
  };

  // ── Helpers for pipeline data display ──
  const fmtAmount = (val) => {
    if (!val) return "—";
    const n = Number(val);
    return isNaN(n) ? val : "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2 });
  };

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "flex-end", zIndex: 1000 }}
        onClick={handleClose}>
        <div style={{ width: "100%", maxWidth: 580, height: "100%", background: C.white, boxShadow: "-8px 0 40px rgba(0,0,0,0.12)", overflowY: "auto", animation: "slideIn 0.25s ease" }}
          onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div style={{ padding: "20px 28px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: C.white, zIndex: 1 }}>
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 2px" }}>Create Quote</h3>
              <div style={{ fontSize: 13, color: C.t2 }}>Add a new quote to the system</div>
            </div>
            <button onClick={handleClose}
              style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", cursor: "pointer", color: C.t3, display: "flex" }}
              onMouseEnter={e => { e.currentTarget.style.color = C.t1; e.currentTarget.style.borderColor = C.t3; }}
              onMouseLeave={e => { e.currentTarget.style.color = C.t3; e.currentTarget.style.borderColor = C.border; }}>
              <Ic.X />
            </button>
          </div>

          {/* Form */}
          <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 18 }}>

            {/* ── Quote Number with Save/Validate ── */}
            <div>
              <label style={labelStyle}>Quote Number <span style={{ color: C.red }}>*</span></label>
              {quoteValidated ? (
                /* Validated state — locked display */
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderRadius: 10, border: "1px solid #86EFAC", background: "#F0FDF4" }}>
                  <span style={{ color: "#16A34A", fontSize: 16, lineHeight: 1 }}>✓</span>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "#15803D" }}>{form.quoteNumber}</span>
                  <button onClick={handleQuoteClear}
                    style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 12, fontFamily: "inherit" }}
                    onMouseEnter={e => e.currentTarget.style.color = C.red}
                    onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                    Change
                  </button>
                </div>
              ) : (
                /* Input state — type + Save/✕ */
                <div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input
                      value={quoteInput}
                      onChange={e => { setQuoteInput(e.target.value); setQuoteError(""); }}
                      onKeyDown={e => { if (e.key === "Enter") handleQuoteValidate(); }}
                      placeholder="e.g. QT-2026-0001"
                      autoFocus
                      style={{ ...inputStyle, flex: 1, margin: 0 }}
                      onFocus={inputFocus} onBlur={inputBlur}
                    />
                    <button onClick={handleQuoteValidate} disabled={quoteValidating || !quoteInput.trim()}
                      style={{ background: C.navy, border: "none", borderRadius: 8, padding: "9px 16px", cursor: quoteValidating || !quoteInput.trim() ? "not-allowed" : "pointer", color: C.white, fontSize: 13, fontWeight: 600, fontFamily: "inherit", opacity: quoteValidating || !quoteInput.trim() ? 0.6 : 1, whiteSpace: "nowrap" }}>
                      {quoteValidating ? "..." : "Save"}
                    </button>
                    {quoteInput && (
                      <button onClick={handleQuoteClear}
                        style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "9px 10px", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        ✕
                      </button>
                    )}
                  </div>
                  {quoteError && (
                    <div style={{ marginTop: 6, fontSize: 12, color: C.red, lineHeight: 1.4 }}>{quoteError}</div>
                  )}
                  <div style={{ marginTop: 5, fontSize: 11, color: C.t3 }}>
                    Enter the quote number and click Save to validate against the Boal Sales Pipeline.
                  </div>
                </div>
              )}
            </div>

            {/* ── Pipeline Data (read-only, shown after validation) ── */}
            {quoteValidated && pipelineData && (
              <div style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 20px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.t3, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                  Pipeline Data
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px" }}>
                  <div>
                    <div style={{ fontSize: 11, color: C.t3, marginBottom: 2 }}>Amount</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.t1 }}>{fmtAmount(pipelineData.amount)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: C.t3, marginBottom: 2 }}>Manufacturer</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.t1 }}>{pipelineData.manufacturer || "—"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: C.t3, marginBottom: 2 }}>Company</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.t1 }}>{pipelineData.company || "—"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: C.t3, marginBottom: 2 }}>Country</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.t1 }}>{pipelineData.country || "—"}</div>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: 11, color: C.t3, marginBottom: 2 }}>Boal Rep</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.t1 }}>{pipelineData.boalRep || "—"}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Linked Request ID — searchable dropdown */}
            <div>
              <label style={labelStyle}>Linked Request ID <span style={{ color: C.t3, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
              <div ref={reqRef} style={{ position: "relative" }}>
                <input
                  value={reqSearch}
                  onChange={e => { setReqSearch(e.target.value); setReqDropdown(true); if (!e.target.value) set("requestId", ""); }}
                  placeholder="Search by Request ID, type, or company..."
                  style={inputStyle} onFocus={e => { inputFocus(e); setReqDropdown(true); }} onBlur={inputBlur}
                />
                {reqDropdown && (
                  <div style={{ position: "absolute", top: "105%", left: 0, right: 0, zIndex: 200, background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.1)", maxHeight: 240, overflowY: "auto" }}>
                    {filteredRequests.length === 0 ? (
                      <div style={{ padding: "14px 16px", fontSize: 13, color: C.t3 }}>No matching requests found.</div>
                    ) : (
                      <>
                        <div style={{ padding: "8px 16px 4px", fontSize: 11, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${C.border}` }}>
                          Select a Request
                        </div>
                        {filteredRequests.map(req => (
                          <button key={req.id} onClick={() => selectRequest(req)}
                            style={{ display: "block", width: "100%", padding: "10px 16px", textAlign: "left", border: "none", background: form.requestId === req.id ? C.grayBg : C.white, cursor: "pointer", fontFamily: "inherit", borderBottom: `1px solid ${C.border}` }}
                            onMouseEnter={e => e.currentTarget.style.background = C.grayBg}
                            onMouseLeave={e => e.currentTarget.style.background = form.requestId === req.id ? C.grayBg : C.white}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: C.info }}>{req.id}</div>
                            <div style={{ fontSize: 12, color: C.t2, marginTop: 1 }}>{req.type} · {req.company} · {req.contactName}</div>
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
              {form.requestId && (
                <div style={{ marginTop: 6, fontSize: 12, color: C.t3 }}>
                  Linked to: <span style={{ color: C.info, fontWeight: 600 }}>{form.requestId}</span>
                  <button onClick={() => { set("requestId", ""); setReqSearch(""); }}
                    style={{ marginLeft: 8, background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 12 }}>× Clear</button>
                </div>
              )}
            </div>

            {/* Expiry Date */}
            <div>
              <label style={labelStyle}>Quote Expiry Date</label>
              <input type="date" value={form.expiryDate} onChange={e => set("expiryDate", e.target.value)}
                style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
            </div>

            {/* Solution Type */}
            <div>
              <label style={labelStyle}>Solution Type</label>
              <input value={form.solutionType} onChange={e => set("solutionType", e.target.value)}
                placeholder="e.g. Clean Agent Total Flooding..."
                style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
            </div>

            {/* Project Name */}
            <div>
              <label style={labelStyle}>Project Name</label>
              <input value={form.projectName} onChange={e => set("projectName", e.target.value)}
                placeholder="Project name"
                style={inputStyle} onFocus={inputFocus} onBlur={inputBlur} />
            </div>

            {/* Quote File — mandatory */}
            <div>
              <label style={labelStyle}>
                Quote File <span style={{ color: C.red }}>*</span>
                <span style={{ color: C.t3, fontWeight: 400, textTransform: "none", letterSpacing: 0, marginLeft: 6 }}>(PDF required)</span>
              </label>
              <FileUpload files={files} onChange={setFiles} />
              {files.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: "#065F46" }}>
                  ✓ {files.length} file{files.length > 1 ? "s" : ""} ready to attach
                </div>
              )}
            </div>

            {error && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: C.red }}>
                {error}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 8 }}>
              <button onClick={handleClose}
                style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.t2, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={handleCreate} disabled={submitting || !quoteValidated}
                style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: C.navy, color: C.white, fontSize: 14, fontWeight: 600, cursor: submitting || !quoteValidated ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: submitting || !quoteValidated ? 0.5 : 1 }}>
                {submitting ? "Creating..." : "Create Quote"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Abandon warning */}
      {showAbandon && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}>
          <div style={{ background: C.white, borderRadius: 16, padding: "28px 32px", maxWidth: 400, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Abandon Quote?</h3>
            <p style={{ fontSize: 14, color: C.t2, margin: "0 0 24px", lineHeight: 1.5 }}>You have unsaved changes. Are you sure you want to leave?</p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowAbandon(false)} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.t2, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Stay</button>
              <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: C.red, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Yes, Abandon</button>
            </div>
          </div>
        </div>
      )}

      {showNoEmailWarning && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200 }}>
          <div style={{ background: C.white, borderRadius: 16, padding: "32px 28px", maxWidth: 420, width: "90%", boxShadow: "0 12px 48px rgba(0,0,0,0.18)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>⚠️</div>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: C.red, margin: 0 }}>No Submitter Email Found</h3>
            </div>
            <p style={{ fontSize: 14, color: C.t2, margin: "0 0 8px", lineHeight: 1.6 }}>
              The quote was created successfully, but no email address was found for the client submitter.
            </p>
            <p style={{ fontSize: 14, color: C.t2, margin: "0 0 24px", lineHeight: 1.6 }}>
              <strong>Please ensure the client is contacted directly</strong> to notify them that their quote is ready.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowNoEmailWarning(false)}
                style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: C.navy, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}
